import {isNil, isNotNil, uniq} from 'ramda';
import {isNotNilOrEmpty} from 'ramda-adjunct';

import i18n from '../../../i18n/i18n';
import {FiltersRequestBody} from '../../../types/api/api';
import {getBetterOrEqualValues} from '../../Filter/components/SavedFilters/utils/getBetterOrEqualValues';

const PERCENTAGE_MULTIPLIER = 100;

type NumericRange = {gte?: number | null; lte?: number | null} | null | undefined;

export type FilterSummaryHelpers = {
  getCarStyle: (carStyle?: string | null) => string;
  getFuelType: (fuelType?: string | null) => string;
  getDrive: (drive?: string | null) => string;
  getTransmission: (transmission?: string | null) => string;
  /** Locale-aware plain number, e.g. 206000 → “206 000”. */
  formatNumber: (value: number) => string | null;
  /** Number with the report currency, e.g. 324500 → “324 500 Kč”. */
  formatPrice: (value: number) => string | null;
};

function formatRange(
  range: NumericRange,
  format: (value: number) => string | null,
  formatLast: (value: number) => string | null = format
): string | null {
  const gte = range?.gte ?? null;
  const lte = range?.lte ?? null;
  if (isNotNil(gte) && isNotNil(lte)) {
    return gte === lte ? formatLast(gte) : `${format(gte)}–${formatLast(lte)}`;
  }
  if (isNotNil(gte)) {
    return `≥ ${formatLast(gte)}`;
  }
  return isNotNil(lte) ? `≤ ${formatLast(lte)}` : null;
}

function labels(
  values: (string | null)[] | null | undefined,
  getLabel: (key?: string | null) => string
): string | null {
  if (isNil(values) || values.length === 0) {
    return null;
  }
  return uniq(values.map((value) => getLabel(value)).filter(isNotNilOrEmpty)).join(' + ');
}

/**
 * A selection covering every possible value of its domain filters nothing —
 * “Manuál + Automat” or “4x2 + 4x4” is no constraint and only adds noise.
 */
function coversFullDomain(values: (string | null)[] | null | undefined, domain: string[]): boolean {
  if (isNil(values) || domain.length === 0) {
    return false;
  }
  const selected = new Set(values.filter(isNotNil));
  return domain.every((value) => selected.has(value));
}

/**
 * Compact, concrete description of an applied filter — resolved values for this
 * vehicle (“2022, Manuál + Automat, Diesel…”), not the relative preset wording
 * (“Same year, Same & better transmission…”).
 */
export function buildFilterSummary(
  filter: FiltersRequestBody,
  helpers: FilterSummaryHelpers
): string[] {
  const {getCarStyle, getFuelType, getDrive, getTransmission, formatNumber, formatPrice} = helpers;
  const parts: (string | null)[] = [];

  parts.push(formatRange(filter.firstRegistrationOnYear, (v) => String(v)));

  const power = formatRange(filter.power, formatNumber);
  parts.push(isNotNil(power) ? `${power} kW` : null);

  const mileage = formatRange(filter.mileage, formatNumber);
  parts.push(isNotNil(mileage) ? `${mileage} km` : null);

  parts.push(
    coversFullDomain(filter.transmission, getBetterOrEqualValues('transmission', null))
      ? null
      : labels(filter.transmission, getTransmission)
  );
  parts.push(labels(filter.fuelType, getFuelType));
  parts.push(labels(filter.bodyStyle, getCarStyle));
  parts.push(
    coversFullDomain(filter.drive, getBetterOrEqualValues('drive', null))
      ? null
      : labels(filter.drive, getDrive)
  );

  if (isNotNil(filter.isVatDeductible)) {
    parts.push(
      filter.isVatDeductible
        ? i18n.t('entity.filterForm.VATDeductible')
        : i18n.t('entity.filterForm.savedFiltersPanel.summary.noVatDeductible')
    );
  }

  parts.push(formatRange(filter.price, formatNumber, formatPrice));

  const featureScore = formatRange(
    isNotNil(filter.featuresLevel)
      ? {
          gte: isNotNil(filter.featuresLevel.gte)
            ? Math.round(filter.featuresLevel.gte * PERCENTAGE_MULTIPLIER)
            : null,
          lte: isNotNil(filter.featuresLevel.lte)
            ? Math.round(filter.featuresLevel.lte * PERCENTAGE_MULTIPLIER)
            : null,
        }
      : null,
    (v) => String(v)
  );
  parts.push(
    isNotNil(featureScore) ? `${i18n.t('entity.filterForm.featureScore')} ${featureScore} %` : null
  );

  const radius = filter.location?.distance;
  parts.push(isNotNil(radius) ? `${i18n.t('entity.filterForm.radius')} ${radius} km` : null);

  const daysOnStock = formatRange(filter.daysOnStock, (v) => String(v));
  parts.push(
    isNotNil(daysOnStock)
      ? `${i18n.t('entity.filterForm.daysInAdvertisement')} ${daysOnStock}`
      : null
  );

  const features = filter.features;
  if (isNotNil(features) && features.length > 0) {
    parts.push(`${i18n.t('entity.filterForm.features')} (${features.length})`);
  }

  if (isNotNilOrEmpty(filter.title)) {
    parts.push(`„${filter.title}“`);
  }

  return parts.filter((part): part is string => isNotNilOrEmpty(part));
}
